/**
 * HoMilestonesTab - Milestones tab for active project detail.
 * Matched exactly to extracted_assets_2: bf832ac1-9d48-45db-a651-9a4805854e40
 */

import { useQueryClient } from "@tanstack/react-query";
import * as React from "react";
import { SkeletonRows } from "@/components/ui/skeleton";
import { formatCalendarDate } from "@/lib/format";
import { queryKeys } from "@/lib/query-keys";
import { useProjectFunding } from "../../../hooks/use-project-funding";
import {
	type Milestone,
	type Task,
	useProject,
	useProjectMilestones,
	useProjectPayments,
	useProjectTasks,
} from "../../../hooks/use-projects";
import {
	type EscrowTransaction,
	updateTaskStatus,
} from "../../../services/projects-api.service";
import {
	DISPUTE_PAYOUT_FAILED_LABEL,
	DISPUTE_PAYOUT_FAILED_NOTE,
	DUPLICATE_CHARGE_LABEL,
	DUPLICATE_CHARGE_NOTE,
	DUPLICATE_CHARGE_REFUNDED_NOTE,
	isDisputePayoutFailed,
	isDuplicateCharge,
	isSplitPartPaid,
	SPLIT_PART_PAID_LABEL,
} from "../../../utils/escrow-balance";
import {
	GENERAL_TASKS_LABEL,
	groupTasksByMilestone,
} from "../../../utils/group-tasks-by-milestone";
import {
	canConfirmCompletion,
	confirmCompletionAndRelease,
} from "../confirm-completion";
import {
	Avatar,
	Btn,
	Eyebrow,
	FloatPanel,
	formatINR as fmt,
	Icn,
	PILL_TONES,
	Pill,
} from "../design-primitives";

type MsStatus = "done" | "active" | "review" | "upcoming";
type MsApproval =
	| "confirmed"
	| "approved"
	| "pending"
	| "locked"
	| "confirming";

// One entry per member of `EscrowTransaction["status"]`, keyed by the status
// itself. The map these replace was reached through a catch-all that sent every
// status other than `released`, `held` and `captured` onto one key labelled
// "Awaiting payment" -- including `refunded`, which told a homeowner whose money
// had been returned that they still owed it.
//
// `pending` is the only status "Awaiting payment" is true of: a payment session
// exists and nobody has paid it. It stays true in the gap between the homeowner
// paying and the provider settling the row. `captured` is money charged and in
// escrow, and `ho-costs-tab` labels it "Funded" too.
//
// A project funding row is written at `pending` and moved to `held` by
// `finalizeProjectFunding`; a contractor assignment is written at `held`
// outright by `hireContractor` in `project-contractors.service.ts`. A payout
// moves a row to `released`, and an admin dispute resolution to
// `refunded`/`partially_refunded`. A `held` row a dispute split has part paid
// is labelled `SPLIT_PART_PAID_LABEL` instead of "In Escrow", and one whose
// dispute resolution's payout failed `DISPUTE_PAYOUT_FAILED_LABEL`, with
// `DISPUTE_PAYOUT_FAILED_NOTE` beneath it.
//
// `failed` never reaches this list: `getProjectPayments` leaves `failed` rows
// out (they are checkouts `supersedeStrandedSession` closed, not payments).
// Its entries keep the maps exhaustive.
const PAY_TONE: Record<EscrowTransaction["status"], keyof typeof PILL_TONES> = {
	pending: "risk",
	captured: "awaiting",
	held: "awaiting",
	released: "done",
	refunded: "neutral",
	partially_refunded: "neutral",
	failed: "risk",
};
const PAY_LABEL: Record<EscrowTransaction["status"], string> = {
	pending: "Awaiting payment",
	captured: "Funded",
	held: "In Escrow",
	released: "Released",
	refunded: "Refunded",
	partially_refunded: "Partially refunded",
	failed: "Payment failed",
};

const APPROVAL: Record<
	string,
	{ tone: keyof typeof PILL_TONES; label: string }
> = {
	confirmed: { tone: "done", label: "You confirmed" },
	approved: { tone: "active", label: "You approved" },
	pending: { tone: "awaiting", label: "Waiting for your approval" },
	locked: { tone: "neutral", label: "Locked — earlier work pending" },
	confirming: { tone: "awaiting", label: "Awaiting your confirmation" },
};

function getMilestoneStatus(milestone: Milestone): MsStatus {
	if (milestone.status === "completed") return "done";
	if (
		milestone.paymentStatus === "paid" &&
		milestone.fundingStatus === "released"
	)
		return "review";
	if (milestone.fundingStatus === "funded") return "active";
	return "upcoming";
}

/**
 * `funded` is checked before `status`, because a milestone someone has already
 * marked completed still has money sitting in escrow until the homeowner
 * confirms, and "You confirmed" would claim they already had. This makes the
 * `confirming` pill and the Confirm completion button the same condition —
 * `fundingStatus === "funded"` — so the pill cannot promise an action the
 * button is not offering.
 */
function getMilestoneApproval(milestone: Milestone): MsApproval {
	if (milestone.fundingStatus === "funded") return "confirming";
	if (milestone.status === "completed") return "confirmed";
	if (milestone.fundingStatus === "unfunded") return "pending";
	if (milestone.fundingStatus === "released") return "approved";
	return "locked";
}

interface UiTask {
	id: string;
	milestoneId: string | null;
	title: string;
	done: boolean;
	ini: string;
	assignee: string;
	by: string;
	due: string;
}

function transformTask(task: Task): UiTask {
	const isDone = task.status === "completed";
	const initials = task.assignedTo
		? task.assigneeDisplayName
				.split(" ")
				.map((n) => n[0])
				.join("")
				.toUpperCase()
				.slice(0, 2)
		: "??";

	const daysUntilDue = task.dueDate
		? Math.ceil(
				(new Date(task.dueDate).getTime() - Date.now()) / (1000 * 60 * 60 * 24),
			)
		: null;

	return {
		id: task.id,
		milestoneId: task.milestoneId,
		title: task.title,
		done: isDone,
		ini: initials,
		assignee: task.assigneeDisplayName,
		by:
			daysUntilDue !== null
				? daysUntilDue > 0
					? `${daysUntilDue} days`
					: "Overdue"
				: "—",
		due: isDone
			? "Done"
			: task.dueDate
				? new Date(task.dueDate).toLocaleDateString("en-GB", {
						day: "2-digit",
						month: "short",
					})
				: "—",
	};
}

/**
 * A list of tasks with a "TASKS · done/total DONE" heading, each task with a
 * checkbox that toggles it between done and to do.
 */
function TaskList({
	tasks,
	accent,
	onToggle,
}: {
	tasks: UiTask[];
	accent: string;
	onToggle: (id: string) => void;
}) {
	return (
		<>
			<Eyebrow style={{ fontSize: 8, marginBottom: 10 }}>
				TASKS · {tasks.filter((t) => t.done).length}/{tasks.length} DONE
			</Eyebrow>
			<div
				style={{
					display: "flex",
					flexDirection: "column",
					gap: 6,
				}}
			>
				{tasks.map((t) => (
					<div
						key={t.id}
						style={{
							display: "flex",
							alignItems: "center",
							gap: 12,
							padding: "8px 10px",
							borderRadius: 6,
							background: "var(--color-dashboard-surface)",
							border: "1px solid var(--color-dashboard-border-soft)",
						}}
					>
						<button
							type="button"
							onClick={() => onToggle(t.id)}
							aria-label={
								t.done
									? `Mark "${t.title}" as incomplete`
									: `Mark "${t.title}" as complete`
							}
							style={{
								width: 20,
								height: 20,
								borderRadius: 4,
								border: 0,
								cursor: "pointer",
								flexShrink: 0,
								display: "flex",
								alignItems: "center",
								justifyContent: "center",
								background: t.done ? accent : "var(--color-dashboard-paper)",
								boxShadow: t.done
									? "none"
									: "inset 0 0 0 1.5px var(--color-border-default)",
							}}
						>
							{t.done && (
								<Icn
									name="check"
									size={13}
									color="var(--color-dashboard-on-fill)"
								/>
							)}
						</button>
						<div style={{ flex: 1, minWidth: 0 }}>
							<div
								style={{
									fontFamily: "var(--font-body)",
									fontSize: 13,
									fontWeight: 600,
									color: t.done
										? "var(--color-dashboard-placeholder)"
										: "var(--color-dashboard-text)",
									textDecoration: t.done ? "line-through" : "none",
								}}
							>
								{t.title}
							</div>
							<div
								style={{
									display: "flex",
									alignItems: "center",
									gap: 7,
									marginTop: 3,
								}}
							>
								<Avatar initials={t.ini} tone="ink" size={16} />
								<span
									style={{
										fontFamily: "var(--font-body)",
										fontSize: 11,
										color: "var(--color-dashboard-muted)",
									}}
								>
									{t.assignee}
								</span>
								<Pill
									tone="neutral"
									dot={false}
									style={{ height: 16, fontSize: 8.5 }}
								>
									{t.by}
								</Pill>
							</div>
						</div>
						<span
							style={{
								fontFamily: "var(--font-body)",
								fontSize: 11.5,
								fontWeight: 600,
								color: "var(--color-dashboard-muted)",
								flexShrink: 0,
							}}
						>
							{t.due}
						</span>
					</div>
				))}
			</div>
		</>
	);
}

interface HoMilestonesTabProps {
	projectId: string;
	accent?: string;
	designerName?: string;
	designerStudio?: string;
	onAskDesigner?: (userId: string) => void;
}

export function HoMilestonesTab({
	projectId,
	accent = "var(--color-dashboard-accent)",
	designerName,
	designerStudio,
	onAskDesigner,
}: HoMilestonesTabProps) {
	const queryClient = useQueryClient();
	const { isFunding, fundMilestones } = useProjectFunding();

	const {
		data: milestonesData,
		isLoading: milestonesLoading,
		refetch: refetchMilestones,
	} = useProjectMilestones(projectId);
	const { data: tasksData, isLoading: tasksLoading } =
		useProjectTasks(projectId);
	const {
		data: paymentsData,
		isLoading: paymentsLoading,
		refetch: refetchPayments,
	} = useProjectPayments(projectId);
	const { data: project } = useProject(projectId);

	const [actionLoading, setActionLoading] = React.useState<string | null>(null);

	const milestones: Milestone[] = milestonesData || [];
	const apiTasks: Task[] = tasksData || [];
	const transactions = paymentsData?.transactions || [];

	const tasks: UiTask[] = React.useMemo(
		() => apiTasks.map(transformTask),
		[apiTasks],
	);
	const groupedTasks = groupTasksByMilestone(tasks, milestones);

	const toggleTask = async (id: string) => {
		const task = tasks.find((t) => t.id === id);
		if (!task) return;
		const newStatus = task.done ? "todo" : "completed";
		const result = await updateTaskStatus(id, newStatus);
		if (result.success) {
			queryClient.invalidateQueries({
				queryKey: queryKeys.projects.tasks(projectId),
			});
		} else {
			console.error("Failed to toggle task status:", result.error);
		}
	};

	// Funding is a checkout, not a single call: the orchestration opens the
	// provider's checkout where one is needed, verifies the order, and surfaces
	// each failure as a toast. Only a payment the server has confirmed reaches
	// `onFunded`, so what the milestone reads as comes back from the refetch
	// rather than from a flag set here.
	const handleApprove = async (milestoneId: string) => {
		setActionLoading(milestoneId);
		try {
			await fundMilestones({
				projectId,
				milestoneIds: [milestoneId],
				onFunded: async () => {
					await Promise.all([refetchMilestones(), refetchPayments()]);
				},
			});
		} finally {
			setActionLoading(null);
		}
	};

	// "Confirm completion" is the homeowner saying the work is done, and in this
	// product that is also what pays the designer -- the banner above the
	// timeline and the escrow note in the costs tab both say escrow releases on
	// the homeowner's confirmation. `confirmCompletionAndRelease` does both, in
	// the order the backend requires.
	const handleConfirm = async (milestone: Milestone) => {
		setActionLoading(milestone.id);
		try {
			await confirmCompletionAndRelease(milestone);
		} finally {
			// Both, whatever happened: a failed release can still have marked the
			// milestone completed.
			refetchMilestones();
			refetchPayments();
			setActionLoading(null);
		}
	};

	// Tasks in no milestone, or in one this tab has not loaded. Shown when the
	// project has no milestones too, so its tasks are not hidden behind the
	// empty state.
	const generalTasksPanel = groupedTasks.general.length > 0 && (
		<section
			aria-label={GENERAL_TASKS_LABEL}
			style={{ marginTop: milestones.length > 0 ? 24 : 0 }}
		>
			<FloatPanel padding={22}>
				<div
					style={{
						fontFamily: "var(--font-display)",
						fontStyle: "italic",
						fontSize: 20,
						fontWeight: 500,
						color: "var(--color-dashboard-text)",
						marginBottom: 16,
					}}
				>
					{GENERAL_TASKS_LABEL}
				</div>
				<TaskList
					tasks={groupedTasks.general}
					accent={accent}
					onToggle={toggleTask}
				/>
			</FloatPanel>
		</section>
	);

	const isLoading = milestonesLoading || tasksLoading || paymentsLoading;

	if (isLoading) {
		return (
			<SkeletonRows
				label="Loading milestones..."
				className="p-10"
				rowClassName="h-16 rounded-xl"
			/>
		);
	}

	if (milestones.length === 0) {
		return (
			<div>
				<div
					style={{
						padding: 40,
						textAlign: "center",
						color: "var(--color-dashboard-muted)",
					}}
				>
					No milestones for this project yet
				</div>
				{generalTasksPanel}
			</div>
		);
	}

	return (
		<div>
			{/* Info banner */}
			<div
				style={{
					display: "flex",
					gap: 10,
					padding: "14px 18px",
					background: "var(--color-dashboard-surface)",
					borderRadius: 8,
					marginBottom: 24,
					alignItems: "flex-start",
				}}
			>
				<Icn
					name="info"
					size={15}
					color={accent}
					style={{ marginTop: 1, flexShrink: 0 }}
				/>
				<span
					style={{
						fontFamily: "var(--font-body)",
						fontSize: 12.5,
						lineHeight: 1.5,
						color: "var(--color-dashboard-muted-dark)",
					}}
				>
					You approve a milestone before its work begins, and confirm it once
					done. Escrow releases to {designerStudio || "the designer"} only on
					your confirmation.
				</span>
			</div>

			{/* Timeline */}
			<div style={{ position: "relative", paddingLeft: 34 }}>
				<div
					style={{
						position: "absolute",
						left: 13,
						top: 8,
						bottom: 8,
						width: 2,
						background: "var(--color-dashboard-border)",
					}}
				/>
				<div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
					{milestones.map((m, idx) => {
						const msStatus = getMilestoneStatus(m);
						const msApproval = getMilestoneApproval(m);
						const canConfirm = canConfirmCompletion(m);
						const done = msStatus === "done";
						const activeM = msStatus === "active" || msStatus === "review";
						const ap = APPROVAL[msApproval] ?? APPROVAL.locked;
						const pays = transactions.filter((t) => t.milestoneId === m.id);
						const milestoneTasks = groupedTasks.byMilestone.get(m.id) ?? [];

						return (
							<div key={m.id} style={{ position: "relative" }}>
								{/* Circle node */}
								<span
									style={{
										position: "absolute",
										left: -34,
										top: 22,
										width: 28,
										height: 28,
										borderRadius: "50%",
										background: done
											? "var(--color-dashboard-success-fill)"
											: activeM
												? accent
												: "var(--color-dashboard-divider)",
										display: "flex",
										alignItems: "center",
										justifyContent: "center",
										boxShadow: "0 0 0 4px var(--color-dashboard-surface)",
										zIndex: 1,
									}}
								>
									{done ? (
										<Icn
											name="check"
											size={15}
											color="var(--color-dashboard-on-fill)"
										/>
									) : (
										<span
											style={{
												fontFamily: "var(--font-mono)",
												fontSize: 11,
												fontWeight: 700,
												color: activeM
													? "var(--color-dashboard-on-fill)"
													: "var(--color-dashboard-muted)",
											}}
										>
											{idx + 1}
										</span>
									)}
								</span>

								{/* Card */}
								<FloatPanel
									elev={
										msApproval === "pending" || msStatus === "review" ? 3 : 1
									}
									padding={22}
									style={{
										borderLeft:
											msApproval === "pending" || msStatus === "review"
												? `3px solid ${accent}`
												: "1px solid var(--color-dashboard-border)",
									}}
								>
									<div
										style={{
											display: "flex",
											justifyContent: "space-between",
											alignItems: "flex-start",
											gap: 20,
											flexWrap: "wrap",
										}}
									>
										{/* Left info */}
										<div style={{ minWidth: 0, flex: 1 }}>
											<div
												style={{
													fontFamily: "var(--font-display)",
													fontStyle: "italic",
													fontSize: 20,
													fontWeight: 500,
													color: "var(--color-dashboard-text)",
												}}
											>
												{m.title}
											</div>
											<div
												style={{
													display: "flex",
													alignItems: "center",
													gap: 10,
													marginTop: 8,
													flexWrap: "wrap",
												}}
											>
												<Pill
													tone={done ? "done" : activeM ? "active" : "neutral"}
												>
													{done
														? "Completed"
														: activeM
															? "In progress"
															: "Upcoming"}
												</Pill>
												<Pill
													tone={
														ap.tone as
															| "done"
															| "active"
															| "awaiting"
															| "neutral"
															| "info"
															| "risk"
													}
													dot={false}
												>
													{ap.label}
												</Pill>
												{m.targetDate && (
													<span
														style={{
															fontFamily: "var(--font-mono)",
															fontSize: 9,
															letterSpacing: "0.1em",
															color: "var(--color-dashboard-muted)",
														}}
													>
														<Icn
															name="calendar"
															size={11}
															color="var(--color-dashboard-muted)"
															style={{ verticalAlign: "-2px", marginRight: 4 }}
														/>
														{formatCalendarDate(
															m.targetDate,
															{ day: "2-digit", month: "short" },
															"en-GB",
														)}
													</span>
												)}
											</div>
											{m.description && (
												<p
													style={{
														fontFamily: "var(--font-body)",
														fontSize: 13,
														lineHeight: 1.55,
														color: "var(--color-dashboard-muted-dark)",
														margin: "12px 0 0",
													}}
												>
													{m.description}
												</p>
											)}
										</div>

										{/* Right: amount */}
										<div style={{ textAlign: "right", flexShrink: 0 }}>
											<Eyebrow style={{ fontSize: 8 }}>MILESTONE VALUE</Eyebrow>
											<div
												style={{
													fontFamily: "var(--font-display)",
													fontWeight: 500,
													fontSize: 24,
													color: "var(--color-dashboard-text)",
													letterSpacing: "-0.015em",
													marginTop: 4,
													fontVariantNumeric: "tabular-nums",
												}}
											>
												{fmt(m.paymentAmount || 0)}
											</div>
										</div>
									</div>

									{/* Payments */}
									{pays.length > 0 && (
										<div
											style={{
												marginTop: 16,
												paddingTop: 16,
												borderTop:
													"1px solid var(--color-dashboard-border-soft)",
											}}
										>
											<Eyebrow style={{ fontSize: 8, marginBottom: 10 }}>
												ESCROW
											</Eyebrow>
											{pays.map((p) => {
												// `??` rather than a defaulted map. The maps stay total
												// on `EscrowTransaction["status"]`, so adding a member to
												// that union is a build error at both of them and at both
												// of `ho-costs-tab`'s. These guards cover the other
												// direction: `status` arrives as JSON, and the union is
												// this frontend's assumption about the server, not a
												// promise from it. A member added to `escrowStatusEnum`
												// and written by some backend path, or a bundle older
												// than the deploy serving it, delivers a status no entry
												// matches -- and `PILL_TONES[undefined]` is undefined, so
												// the row's `pt.dot` throws, through to the only
												// ErrorBoundary above it (the root one in `App.tsx`),
												// which replaces the whole app with "Something went
												// wrong".
												//
												// The label falls back to the raw status rather than to a
												// chosen word, deliberately. A default of "Pending" or
												// "Awaiting payment" is exactly the collapsing these maps
												// replaced: it asserts a location for the homeowner's
												// money on a status nobody has decided about, which is
												// how a `refunded` row came to read as one still owed. A
												// raw status reads as unfinished and claims nothing.
												const tone = PAY_TONE[p.status] ?? "neutral";
												const pt = PILL_TONES[tone];
												const payoutFailed = isDisputePayoutFailed(p);
												const duplicateOf = isDuplicateCharge(p);
												const duplicate = duplicateOf && p.status === "held";
												const label = duplicate
													? DUPLICATE_CHARGE_LABEL
													: payoutFailed
														? DISPUTE_PAYOUT_FAILED_LABEL
														: isSplitPartPaid(p)
															? SPLIT_PART_PAID_LABEL
															: (PAY_LABEL[p.status] ?? p.status);
												return (
													<div
														key={p.id}
														style={{
															display: "flex",
															alignItems: "center",
															gap: 12,
															padding: "10px 12px",
															borderRadius: 7,
															background: "var(--color-dashboard-surface)",
															border:
																"1px solid var(--color-dashboard-border-soft)",
															marginBottom: 6,
														}}
													>
														<span
															style={{
																width: 8,
																height: 8,
																borderRadius: "50%",
																background: pt.dot,
																flexShrink: 0,
															}}
														/>
														<div style={{ flex: 1, minWidth: 0 }}>
															<div
																style={{
																	fontFamily: "var(--font-body)",
																	fontSize: 13,
																	fontWeight: 600,
																	color: "var(--color-dashboard-text)",
																}}
															>
																{p.milestoneTitle || "Milestone Payment"}
															</div>
															<div
																style={{
																	fontFamily: "var(--font-mono)",
																	fontSize: 8.5,
																	letterSpacing: "0.08em",
																	color: "var(--color-dashboard-muted)",
																	marginTop: 2,
																	textTransform: "uppercase",
																}}
															>
																Bank transfer ·{" "}
																{p.createdAt
																	? new Date(p.createdAt).toLocaleDateString(
																			"en-GB",
																			{
																				day: "2-digit",
																				month: "short",
																				year: "numeric",
																			},
																		)
																	: "—"}
															</div>
															{(payoutFailed || duplicateOf) && (
																<div
																	style={{
																		fontFamily: "var(--font-body)",
																		fontSize: 12,
																		color:
																			"var(--color-dashboard-pill-awaiting)",
																		marginTop: 6,
																		lineHeight: 1.45,
																	}}
																>
																	{duplicate
																		? DUPLICATE_CHARGE_NOTE
																		: duplicateOf
																			? DUPLICATE_CHARGE_REFUNDED_NOTE
																			: DISPUTE_PAYOUT_FAILED_NOTE}
																</div>
															)}
														</div>
														<div style={{ textAlign: "right" }}>
															<div
																style={{
																	fontFamily: "var(--font-ui)",
																	fontSize: 14,
																	fontWeight: 700,
																	color: "var(--color-dashboard-text)",
																}}
															>
																{fmt(p.amount)}
															</div>
															<Pill
																tone={tone}
																dot={false}
																style={{
																	height: 18,
																	fontSize: 9,
																	marginTop: 3,
																}}
															>
																{label}
															</Pill>
														</div>
													</div>
												);
											})}
										</div>
									)}

									{/* Tasks */}
									{milestoneTasks.length > 0 && (
										<section
											aria-label={`Tasks for ${m.title}`}
											style={{
												marginTop: 16,
												paddingTop: 16,
												borderTop:
													"1px solid var(--color-dashboard-border-soft)",
											}}
										>
											<TaskList
												tasks={milestoneTasks}
												accent={accent}
												onToggle={toggleTask}
											/>
										</section>
									)}

									{/* Actions */}
									{(msApproval === "pending" ||
										msStatus === "review" ||
										canConfirm) && (
										<div
											style={{
												display: "flex",
												gap: 10,
												marginTop: 16,
												paddingTop: 16,
												borderTop:
													"1px solid var(--color-dashboard-border-soft)",
												flexWrap: "wrap",
											}}
										>
											{msApproval === "pending" && (
												<Btn
													variant="primary"
													size="sm"
													icon={<Icn name="check" size={16} color="white" />}
													disabled={isFunding}
													onClick={() => handleApprove(m.id)}
												>
													{actionLoading === m.id
														? "Funding..."
														: "Approve & fund milestone"}
												</Btn>
											)}
											{canConfirm && (
												<Btn
													variant="primary"
													size="sm"
													icon={
														<Icn name="checkCircle" size={16} color="white" />
													}
													// Disabled while this tab has an action in flight, read from
													// `actionLoading`, which `handleConfirm` holds for its whole
													// run. Not the `isFunding` flag "Approve & fund milestone" uses:
													// that one belongs to the funding hook and says nothing about a
													// release. Without this a double click sent two
													// `releaseMilestonePayment` calls, and the second cannot succeed:
													// the payout the first one made leaves no escrow row at `held`
													// for it to find, so its failure is reported to the homeowner as
													// a failed confirmation of a release that worked.
													disabled={actionLoading !== null}
													onClick={() => handleConfirm(m)}
												>
													{/* Not "Releasing...": the first of the two calls this
													    makes marks the milestone completed, and it is the
													    one that can still fail with nothing released. */}
													{actionLoading === m.id
														? "Confirming..."
														: "Confirm completion"}
												</Btn>
											)}
											<Btn
												variant="ghost"
												size="sm"
												icon={<Icn name="message" size={13} />}
												onClick={() => {
													if (project?.designer?.userId && onAskDesigner) {
														onAskDesigner(project.designer.userId);
													}
												}}
											>
												Ask {designerName || "the designer"}
											</Btn>
										</div>
									)}
								</FloatPanel>
							</div>
						);
					})}
				</div>
			</div>

			{generalTasksPanel}
		</div>
	);
}
